export const NARRATIVE = { heading: 1000, readEnd: 3800, contentEnd: 4800 } as const;
export const clamp = (n: number): number => Math.max(0, Math.min(1, Number.isFinite(n) ? n : 0));
export const ease = (n: number): number => { const t = clamp(n); return t*t*(3-2*t); };
export function sampleNarrative(ms: number, reduced = false) {
  const t = reduced ? 1e6 : Math.max(0, ms);
  return { heading: ease(t / NARRATIVE.heading), reading: ease((t-1000)/2800), content: ease((t-3800)/1000), stage: t < 1000 ? 'heading' : t < 3800 ? 'reading' : 'content' } as const;
}
export const CLOSER_DURATION=11800;
export function sampleCloser(ms:number,reduced=false){
 const t=reduced?CLOSER_DURATION:Math.max(0,ms);
 return{first:ease(t/1200)*(1-ease((t-4500)/1000)),second:ease((t-5600)/1400),reading:ease((t-1200)/2800),park:ease((t-4000)/700),handoff:ease((t-4900)/3000),emphasis:t<5500?ease(t/1800)*(1-ease((t-2700)/2000)):ease((t-5600)/2000)*(1-ease((t-8100)/2700))};
}
export function samplePlans(ms: number, reduced = false) {
  const t = reduced ? 1e6 : Math.max(0, ms);
  return {
    reveal: Math.min(4, Math.max(0, Math.floor((t-4300)/1600)+1)),
    inspect: reduced ? -1 : samplePlanInspection(t).inspect,
    year: t < 9300 ? 'white' : t < 10000 ? 'orange' : t < 10500 ? 'glow' : 'badge',
    trial: t >= 11200,
  } as const;
}
/** One considered presentation, left to right, ending at the longest term. */
export function samplePlanInspection(ms:number):InspectionSample{
 return sampleInspection(ms,4,3950,1600,650);
}

export interface InspectionSample {
  readonly from: number;
  readonly to: number;
  readonly transfer: number;
  readonly inspect: number;
}

/** -1 is the heading's parking point, not a product card. */
export function sampleInspection(ms: number, count: number, start: number, stride: number, travel: number): InspectionSample {
  const t = Number.isFinite(ms) ? ms : 0;
  if (count <= 0 || t < start) return { from: -1, to: -1, transfer: 1, inspect: -1 };
  const elapsed = t - start;
  if (elapsed >= count * stride) return { from: count - 1, to: count - 1, transfer: 1, inspect: -1 };
  const to = Math.min(count - 1, Math.floor(elapsed / stride));
  const local = elapsed - to * stride;
  return { from: to - 1, to, transfer: ease(local / travel), inspect: local < travel ? -1 : to };
}

export interface NarrativePoint { readonly x: number; readonly y: number }
export function inspectionPoint(sample: InspectionSample, cards: readonly NarrativePoint[], park: NarrativePoint): NarrativePoint {
  const from = cards[sample.from] ?? park, to = cards[sample.to] ?? park;
  return { x: from.x + (to.x - from.x) * sample.transfer, y: from.y + (to.y - from.y) * sample.transfer };
}

export function sampleFinale(progress: number, active: boolean, reduced = false) {
  const world = active ? reduced ? 1 : clamp(progress) : 0;
  return { progress: world, footer: ease((world - (1-450/8000)) / (450/8000)) };
}

export function canAdvanceNarrative(active: boolean, onStage: boolean, content: {top:number;bottom:number}, title: {top:number;bottom:number} | undefined, viewport: number, started: boolean): boolean {
  if (!active || !onStage || content.bottom <= viewport * .12 || content.top >= viewport * .7) return false;
  return started || Boolean(title && title.bottom > viewport * .1 && title.top < viewport * .7);
}

/** Paused offscreen; a reverse visit retains a complete coherent scene.
 * Only crossing back before the block starts another visit. No wall-clock
 * catch-up can run content while its title is hidden. */
export class NarrativeClock {
  elapsed = 0;
  private last: number | null = null;
  tick(now: number, visible: boolean, before: boolean): number {
    if (before) { this.elapsed = 0; this.last = null; return this.elapsed; }
    if (!Number.isFinite(now)) { this.last = null; return this.elapsed; }
    if (visible && this.last !== null) this.elapsed += Math.max(0, Math.min(100, now-this.last));
    this.last = visible ? now : null;
    return this.elapsed;
  }
}
